import type { ReactNode } from 'react'

/**
 * Every table's "Acciones" column used bare text links (text-{color}-600,
 * no padding, separated only by mr-3) -- a ~20px effective tap height, well
 * under the ~44px touch-target guideline, with adjacent actions close
 * enough to mis-tap on a phone. This wraps the same semantic colors already
 * used everywhere (terracotta for the primary/edit action, green for a
 * positive action, red for destructive/toggle-off, espresso for a neutral
 * one) in a real padded hit target -- the negative vertical margin absorbs
 * the added padding so table row height doesn't visibly grow, while the
 * actual clickable/tappable box still does.
 */
const VARIANT_STYLES = {
  primary: 'text-terracotta-700 hover:text-terracotta-900 focus-visible:ring-terracotta-500',
  success: 'text-green-700 hover:text-green-900 focus-visible:ring-green-500',
  danger: 'text-red-600 hover:text-red-900 focus-visible:ring-red-500',
  neutral: 'text-espresso-600 hover:text-espresso-900 focus-visible:ring-espresso-500',
} as const

export type TableActionButtonVariant = keyof typeof VARIANT_STYLES

interface TableActionButtonProps {
  onClick: () => void
  variant?: TableActionButtonVariant
  disabled?: boolean
  title?: string
  children: ReactNode
}

export default function TableActionButton({
  onClick,
  variant = 'primary',
  disabled = false,
  title,
  children,
}: TableActionButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`inline-flex items-center rounded-md px-2.5 py-2 -my-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-black/5 focus:outline-none focus-visible:ring-2 ${VARIANT_STYLES[variant]}`}
    >
      {children}
    </button>
  )
}
